/**
 * Change your North Bank name. Tap the handle chip to open; type a name and
 * the chip checks availability live, then claims it. Uniqueness and profanity
 * are the server's call — this just gives instant, honest feedback.
 */
import { useEffect, useRef, useState } from 'react';
import { checkHandle, claimHandle, downloadMyData, eraseMyData } from '../lib/api';
import { MAX_HANDLE, MIN_HANDLE, isValidCustomHandle, normalizeHandle } from '../lib/handle';
import { haptic } from './Floodlight';

type Status = 'idle' | 'checking' | 'ok' | 'taken' | 'invalid' | 'consent' | 'saving';

export const HandleEditor = ({
  current,
  onChange,
  participationAllowed,
  onRequireParticipation,
}: {
  current: string;
  onChange: (handle: string) => void;
  participationAllowed: boolean;
  onRequireParticipation: (action: string, onComplete: () => void, onSuccessFocus?: () => void) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(current);
  const [status, setStatus] = useState<Status>('idle');
  const [eraseStatus, setEraseStatus] = useState<'idle' | 'confirming' | 'erasing' | 'error'>('idle');
  const [exportStatus, setExportStatus] = useState<'idle' | 'exporting' | 'error'>('idle');
  const debounce = useRef<ReturnType<typeof setTimeout>>();
  const keepDataRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (eraseStatus === 'confirming') keepDataRef.current?.focus(); }, [eraseStatus]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue(current);
      setStatus('idle');
      const frame = requestAnimationFrame(() => {
        // A quick erasure action can open its confirmation before this frame.
        // Do not let delayed name-field focus steal the safe cancel control.
        if (!keepDataRef.current) inputRef.current?.focus();
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [open, current]);

  const onType = (next: string) => {
    setValue(next);
    clearTimeout(debounce.current);
    const trimmed = normalizeHandle(next);
    if (trimmed === current) {
      setStatus('idle');
      return;
    }
    if (!isValidCustomHandle(next)) {
      setStatus('invalid');
      return;
    }
    setStatus('checking');
    debounce.current = setTimeout(async () => {
      const { available } = await checkHandle(next);
      setStatus(available ? 'ok' : 'taken');
    }, 360);
  };

  const commitSave = async () => {
    if (status === 'saving') return;
    const trimmed = normalizeHandle(value);
    if (trimmed === current) {
      setOpen(false);
      return;
    }
    if (!isValidCustomHandle(value)) {
      setStatus('invalid');
      return;
    }
    setStatus('saving');
    const res = await claimHandle(value);
    if (res.ok) {
      haptic();
      onChange(res.handle);
      setOpen(false);
    } else {
      setStatus(res.error === 'taken' ? 'taken' : res.error === 'consent' ? 'consent' : 'invalid');
    }
  };

  const save = () => {
    if (!participationAllowed) {
      onRequireParticipation('change your supporter name', () => { void commitSave(); }, () => inputRef.current?.focus());
      return;
    }
    void commitSave();
  };

  const erase = async () => {
    if (eraseStatus === 'erasing') return;
    if (eraseStatus !== 'confirming') { setEraseStatus('confirming'); return; }
    setEraseStatus('erasing');
    if (!(await eraseMyData())) {
      setEraseStatus('error');
      return;
    }
    window.location.reload();
  };

  const exportData = async () => {
    if (exportStatus === 'exporting') return;
    setExportStatus('exporting');
    setExportStatus(await downloadMyData() ? 'idle' : 'error');
  };

  const len = normalizeHandle(value).length;
  const canSave = (status === 'ok' || (status === 'idle' && len >= MIN_HANDLE)) && normalizeHandle(value) !== current;

  const hint =
    status === 'checking'
      ? 'Checking…'
      : status === 'ok'
        ? 'That name is free'
        : status === 'taken'
          ? 'Another Gunner has that name'
          : status === 'invalid'
            ? `${MIN_HANDLE}–${MAX_HANDLE} letters, numbers, spaces`
            : status === 'consent'
              ? 'Confirm the North Bank rules before changing your public name'
            : status === 'saving'
              ? 'Saving…'
              : 'Pick a name the North Bank will know you by';

  if (!open) {
    return (
      <button
        className="handle-chip handle-chip--btn"
        onClick={() => setOpen(true)}
        title="Change your North Bank name"
      >
        {current.length > 12 ? current.slice(0, 11) + '…' : current}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      </button>
    );
  }

  return (
    <div className={`handle-edit handle-edit--${status}`}>
      <div className="handle-edit-row">
        <input
          ref={inputRef}
          className="handle-edit-input"
          value={value}
          maxLength={MAX_HANDLE}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) save();
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder="Your name"
          aria-label="Your North Bank name"
        />
        <button className="handle-edit-save" disabled={!canSave} onClick={save}>
          {status === 'saving' ? '…' : 'Save'}
        </button>
        <button className="handle-edit-cancel" onClick={() => setOpen(false)} aria-label="Cancel">
          ✕
        </button>
      </div>
      <span className="handle-edit-hint">{hint}</span>
      <div className="handle-privacy">
        <button className="handle-erase" type="button" disabled={exportStatus === 'exporting'} onClick={exportData}>
          {exportStatus === 'exporting' ? 'Preparing…' : 'Download my COYG data'}
        </button>
        {eraseStatus === 'confirming' ? <div className="handle-erase-confirm" role="group" aria-label="Confirm permanent erasure">
          <p role="alert">Erase your COYG name, takes, replies, reactions, reports, predictions, Gauge entries, consent and safety requests from this device? This cannot be undone.</p>
          <button ref={keepDataRef} className="handle-erase" type="button" onClick={() => setEraseStatus('idle')}>Keep my data</button>
          <button className="handle-erase" type="button" onClick={erase}>Erase permanently</button>
        </div> : <button className="handle-erase" type="button" disabled={eraseStatus === 'erasing'} onClick={erase}>
          {eraseStatus === 'erasing' ? 'Erasing…' : 'Erase my COYG data'}
        </button>}
        <span>{exportStatus === 'error' ? 'The download did not finish. Try again when COYG is available.' : eraseStatus === 'error' ? 'Erasure did not complete. Your data is still here. Try again when COYG is available.' : 'Portable export or permanent erasure.'}</span>
      </div>
    </div>
  );
};
